import React, { FC, RefObject, useEffect, useRef, useState } from 'react'
import SelectInput from '@components/requestForms/inputs/select.inputs'
import requestForm from '@components/requestForms/request.form'
import { REQUEST_CONFIGS_KEYS } from '@services/constants'
import { RequestFormProps, RequestInputs, SelectList } from '@utils/request'
import TextInput from '@components/requestForms/inputs/text.inputs'
import Notification from '@components/ui/notification'

type PropertyCollectionHousing = {
	propertyType: string
	collectionSector: string
	collectionHousing: string
}

const NewBinAddOrChangeForm: FC<RequestFormProps> = ({
	inputs,
	errors,
	onInputsChange,
	onFixError
}) => {
	const {
		pageAssets,
		UpdateRequestInputsArray,
		UpdateRequestInputsErrorsArray
	} = requestForm()

	const [newBinInput, setNewBinInput] = useState<RequestInputs>({
		name: 'newBin',
		value: '',
		label: pageAssets?.request_form_newBinAddOrChange_newBin_label,
		labelKey: 'request_form_newBinAddOrChange_newBin_label',
		required: true,
		ref: useRef<HTMLSelectElement>(null)
	})

	const [propertyCollectionHousingInput, setPropertyCollectionHousingInput] =
		useState<RequestInputs>({
			name: 'propertyCollectionHousing',
			label: pageAssets?.request_form_newBinAddOrChange_collectionHousing_label,
			labelKey: 'request_form_newBinAddOrChange_collectionHousing_label',
			value: '',
			valueKey: '',
			required: true,
			ref: useRef<HTMLSelectElement>(null)
		})

	const [collectionHousingInput, setCollectionHousingInput] =
		useState<RequestInputs>({
			name: 'collectionHousing',
			value: '',
			valueKey: '',
			label: pageAssets?.request_form_newBinAddOrChange_collectionHousing_label,
			labelKey: 'request_form_newBinAddOrChange_collectionHousing_label',
			required: true
		})

	const [collectionSectorInput, setCollectionSectorInput] =
		useState<RequestInputs>({
			name: 'collectionSector',
			label: '',
			value: '',
			labelKey: '',
			required: true
		})

	const [containerCollectionInput, setContainerCollectionInput] =
		useState<RequestInputs>({
			name: 'containerCollection',
			value: '',
			label:
				pageAssets?.request_form_newBinAddOrChange_containerCollection_label,
			labelKey: 'request_form_newBinAddOrChange_containerCollection_label',
			required: true,
			ref: useRef<HTMLSelectElement>(null)
		})

	const [numberOnTheBoxInput, setNumberOnTheBoxInput] = useState<RequestInputs>(
		{
			name: 'numberOnTheBox',
			value: '',
			valueKey: '',
			label: pageAssets?.request_form_newBinAddOrChange_numberOnTheBox_label,
			labelKey: 'request_form_newBinAddOrChange_numberOnTheBox_label',
			required: false,
			isSentInDetails: true
		}
	)

	// const [brandOfContainerInput, setBrandOfContainerInput] =
	// 	useState<RequestInputs>({
	// 		name: 'brandOfContainer',
	// 		value: '',
	// 		valueKey: '',
	// 		label: pageAssets?.request_form_newBinAddOrChange_brandOfContainer_label,
	// 		labelKey: 'request_form_newBinAddOrChange_brandOfContainer_label',
	// 		required: false
	// 	})

	const newBinList: SelectList[] = [
		{
			label: '',
			value: '',
			key: ''
		},
		{
			label: pageAssets?.request_form_select_newBin_address,
			value: REQUEST_CONFIGS_KEYS.newBin.newAddress,
			key: 'request_form_select_newBin_address'
		},
		{
			label: pageAssets?.request_form_select_newBin_additional,
			value: REQUEST_CONFIGS_KEYS.newBin.additionalBin,
			key: 'request_form_select_newBin_additional'
		},
		{
			label: pageAssets?.request_form_select_newBin_volumeChange,
			value: REQUEST_CONFIGS_KEYS.newBin.volumeChange,
			key: 'request_form_select_newBin_volumeChange'
		}
	]

	const convertPropertyCollectionHousingToString = (
		propertyType: string,
		collectionSector: string,
		collectionHousing: string
	): string =>
		JSON.stringify({ propertyType, collectionSector, collectionHousing })

	const collectionHousingList: SelectList[] = [
		{
			label: '',
			value: '',
			key: ''
		},
		{
			label:
				pageAssets?.select_propertyCollectionHousing_privateResidentialSevenLess,
			value: convertPropertyCollectionHousingToString(
				REQUEST_CONFIGS_KEYS.property.private,
				REQUEST_CONFIGS_KEYS.sector.residential,
				REQUEST_CONFIGS_KEYS.housing.sevenAndLess
			),
			key: 'select_propertyCollectionHousing_privateResidentialSevenLess'
		},
		{
			label:
				pageAssets?.select_propertyCollectionHousing_privateResidentialEightOrTwentyThree,
			value: convertPropertyCollectionHousingToString(
				REQUEST_CONFIGS_KEYS.property.private,
				REQUEST_CONFIGS_KEYS.sector.residential,
				REQUEST_CONFIGS_KEYS.housing.eightAndTwentyThree
			),
			key: 'select_propertyCollectionHousing_privateResidentialEightMore'
		},
		{
			label:
				pageAssets?.select_propertyCollectionHousing_privateResidentialTwentyFourMore,
			value: convertPropertyCollectionHousingToString(
				REQUEST_CONFIGS_KEYS.property.private,
				REQUEST_CONFIGS_KEYS.sector.residential,
				REQUEST_CONFIGS_KEYS.housing.twentyFourAndMOre
			),
			key: 'select_propertyCollectionHousing_privateResidentialTwentyFourMore'
		},
		{
			label: pageAssets?.select_propertyCollectionHousing_privateIndustryOthers,
			value: convertPropertyCollectionHousingToString(
				REQUEST_CONFIGS_KEYS.property.private,
				REQUEST_CONFIGS_KEYS.sector.industry,
				REQUEST_CONFIGS_KEYS.housing.industry
			),
			key: 'select_propertyCollectionHousing_privateIndustryOthers'
		},
		{
			label:
				pageAssets?.select_propertyCollectionHousing_privateInstitutionOthers,
			value: convertPropertyCollectionHousingToString(
				REQUEST_CONFIGS_KEYS.property.private,
				REQUEST_CONFIGS_KEYS.sector.institution,
				REQUEST_CONFIGS_KEYS.housing.institution
			),
			key: 'select_propertyCollectionHousing_privateInstitutionOthers'
		}
	]

	const containerCollectionList: SelectList[] = [
		{
			label: '',
			value: '',
			key: ''
		},
		{
			label: pageAssets?.select_binType_recycling,
			value: REQUEST_CONFIGS_KEYS.binType.recycling,
			key: 'select_binType_recycling'
		},
		{
			label: pageAssets?.select_binType_compost,
			value: REQUEST_CONFIGS_KEYS.binType.compost,
			key: 'select_binType_compost'
		},
		{
			label: pageAssets?.select_binType_garbage,
			value: REQUEST_CONFIGS_KEYS.binType.garbage,
			key: 'select_binType_garbage'
		}
	]

	const onChangeNewBinInput = (value: string, valueKey: string) => {
		setNewBinInput({ ...newBinInput, value, valueKey })

		if (value == REQUEST_CONFIGS_KEYS.newBin.volumeChange) {
			setNumberOnTheBoxInput({ ...numberOnTheBoxInput, required: true })
		} else {
			setNumberOnTheBoxInput({
				...numberOnTheBoxInput,
				value: '',
				required: false
			})
			// setBrandOfContainerInput({ ...brandOfContainerInput, value: '' })
		}

		onFixError(
			errors.filter(
				(error) => ![newBinInput.name, numberOnTheBoxInput.name].includes(error)
			)
		)
	}

	const onChangePropertyCollectionHousingInput = (
		value: string,
		valueKey: string
	) => {
		setPropertyCollectionHousingInput({
			...propertyCollectionHousingInput,
			value,
			valueKey
		})

		onUpdatePropertyCollectionHousingFields(value, valueKey)

		onFixError(
			errors.filter(
				(error) =>
					![
						propertyCollectionHousingInput.name,
						collectionSectorInput.name,
						collectionHousingInput.name
					].includes(error)
			)
		)
	}

	const onUpdatePropertyCollectionHousingFields = (
		value: string,
		valueKey: string
	) => {
		if (value === '') {
			setCollectionSectorInput({
				...collectionSectorInput,
				value,
				valueKey: ''
			})
			setCollectionHousingInput({
				...collectionHousingInput,
				value,
				valueKey: ''
			})

			return
		}

		const fields: PropertyCollectionHousing = JSON.parse(value)

		setCollectionSectorInput({
			...collectionSectorInput,
			value: fields.collectionSector,
			valueKey: valueKey
		})
		setCollectionHousingInput({
			...collectionHousingInput,
			value: fields.collectionHousing,
			valueKey: valueKey
		})
	}

	const onChangeContainerCollectionInput = (
		value: string,
		valueKey: string
	) => {
		setContainerCollectionInput({
			...containerCollectionInput,
			value,
			valueKey
		})
		onFixError(
			UpdateRequestInputsErrorsArray(errors, containerCollectionInput.name)
		)
	}

	const onChangeNumberOnTheBoxInput = (value: string) => {
		setNumberOnTheBoxInput({ ...numberOnTheBoxInput, value })
	}

	// const onChangeBrandOfContainerInput = (value: string) => {
	// 	setBrandOfContainerInput({ ...brandOfContainerInput, value })
	// }

	useEffect(() => {
		onInputsChange(UpdateRequestInputsArray(inputs, newBinInput))
	}, [newBinInput])

	useEffect(() => {
		onInputsChange(UpdateRequestInputsArray(inputs, containerCollectionInput))
	}, [containerCollectionInput])

	useEffect(() => {
		onInputsChange(
			UpdateRequestInputsArray(inputs, propertyCollectionHousingInput)
		)
	}, [propertyCollectionHousingInput])

	useEffect(() => {
		onInputsChange(UpdateRequestInputsArray(inputs, collectionHousingInput))
	}, [collectionHousingInput])

	useEffect(() => {
		onInputsChange(UpdateRequestInputsArray(inputs, collectionSectorInput))
	}, [collectionSectorInput])

	useEffect(() => {
		onInputsChange(UpdateRequestInputsArray(inputs, numberOnTheBoxInput))
	}, [numberOnTheBoxInput])

	// useEffect(() => {
	// 	onInputsChange(UpdateRequestInputsArray(inputs, brandOfContainerInput))
	// }, [brandOfContainerInput])

	return (
		<>
			<Notification
				type="info"
				text={pageAssets?.request_form_newBinAddOrChange_notification_info}
				hasHtml
			/>
			<SelectInput
				id={newBinInput.name}
				label={newBinInput.label}
				value={newBinInput.value}
				list={newBinList}
				onChange={onChangeNewBinInput}
				hasError={errors.includes(newBinInput.name)}
				required
				ref={newBinInput.ref as RefObject<HTMLSelectElement>}
			/>

			<SelectInput
				id={propertyCollectionHousingInput.name}
				label={propertyCollectionHousingInput.label}
				value={propertyCollectionHousingInput.value}
				list={collectionHousingList}
				onChange={onChangePropertyCollectionHousingInput}
				hasError={errors.includes(propertyCollectionHousingInput.name)}
				required
				ref={propertyCollectionHousingInput.ref as RefObject<HTMLSelectElement>}
			/>

			<SelectInput
				id={containerCollectionInput.name}
				label={containerCollectionInput.label}
				value={containerCollectionInput.value}
				list={containerCollectionList}
				onChange={onChangeContainerCollectionInput}
				hasError={errors.includes(containerCollectionInput.name)}
				required
				ref={containerCollectionInput.ref as RefObject<HTMLSelectElement>}
			/>

			{newBinInput.value === REQUEST_CONFIGS_KEYS.newBin.volumeChange && (
				<>
					<TextInput
						id={numberOnTheBoxInput.name}
						label={numberOnTheBoxInput.label}
						value={numberOnTheBoxInput.value}
						onChange={onChangeNumberOnTheBoxInput}
						hasError={errors.includes(numberOnTheBoxInput.name)}
						required={numberOnTheBoxInput.required}
						ref={numberOnTheBoxInput.ref as RefObject<HTMLInputElement>}
					/>

					{/* <TextInput
						id={brandOfContainerInput.name}
						label={brandOfContainerInput.label}
						value={brandOfContainerInput.value}
						onChange={onChangeBrandOfContainerInput}
					/> */}
				</>
			)}
		</>
	)
}

export default NewBinAddOrChangeForm
